import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useUser } from "../context/UserContext";
import { fmtVND, fmtDate } from "../lib/format";
import StatusChip from "../components/StatusChip";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "earning", label: "Earnings" },
  { key: "withdrawal", label: "Withdrawals" },
  { key: "adjustment", label: "Adjustments" },
  { key: "reversal", label: "Reversals" },
];

export default function Activity() {
  const { activeUserId } = useUser();
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeUserId) return;
    setLoading(true);
    api.ledger(activeUserId, filter).then((l) => {
      setEntries(l);
      setLoading(false);
    });
  }, [activeUserId, filter]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">Activity</h1>
        <p className="text-sm text-slate-500 mt-1">
          Full ledger with running balance.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
              filter === f.key
                ? "border-teal-700 bg-teal-700 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Description</th>
              <th className="px-4 py-3 text-left font-semibold hidden sm:table-cell">Date</th>
              <th className="px-4 py-3 text-left font-semibold hidden md:table-cell">Status</th>
              <th className="px-4 py-3 text-right font-semibold">Amount</th>
              <th className="px-4 py-3 text-right font-semibold hidden sm:table-cell">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                  No entries match this filter.
                </td>
              </tr>
            )}
            {entries.map((e) => (
              <tr
                key={e.id}
                data-testid={`ledger-row-${e.id}`}
                className="hover:bg-slate-50 transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`h-2 w-2 rounded-full shrink-0 ${
                        e.direction === "credit" ? "bg-emerald-500" : "bg-slate-400"
                      }`}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {e.description}
                      </p>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider">
                        {e.type}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 hidden sm:table-cell whitespace-nowrap">
                  {fmtDate(e.created_at)}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <StatusChip status={e.status} />
                </td>
                <td
                  className={`px-4 py-3 text-right font-semibold tabular-nums ${
                    e.direction === "credit" ? "text-emerald-700" : "text-slate-900"
                  }`}
                >
                  {e.direction === "credit" ? "+" : "−"} {fmtVND(e.amount_vnd)}
                </td>
                <td className="px-4 py-3 text-right text-xs text-slate-500 tabular-nums hidden sm:table-cell">
                  {fmtVND(e.running_balance_vnd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
